import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const { api } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const poll = async (attempt) => {
      if (attempt >= maxAttempts) { setStatus('timeout'); return; }
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else {
          setAttempts(attempt + 1);
          setTimeout(() => poll(attempt + 1), 2000);
        }
      } catch {
        setStatus('error');
      }
    };
    poll(0);
  };

  return (
    <div className="page-container flex items-center justify-center min-h-screen" data-testid="payment-success-page">
      <div className="text-center max-w-md mx-auto px-4">
        {status === 'checking' && (
          <div className="animate-fade-in">
            <Loader2 className="h-16 w-16 text-[#1a5c5c] animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Processing Payment</h2>
            <p className="text-slate-500">Verifying your payment... (Attempt {attempts + 1}/5)</p>
          </div>
        )}
        {status === 'success' && (
          <div className="animate-fade-in-up">
            <CheckCircle className="h-20 w-20 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Payment Successful!</h2>
            <p className="text-slate-500 mb-6">Your payment has been processed successfully. Thank you!</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </Button>
          </div>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="animate-fade-in">
            <XCircle className="h-20 w-20 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
              {status === 'timeout' ? 'Payment Pending' : 'Payment Issue'}
            </h2>
            <p className="text-slate-500 mb-6">
              {status === 'timeout' ? 'Payment is still being processed. Check your email for confirmation.' : 'There was an issue with your payment. Please try again.'}
            </p>
            <Button onClick={() => navigate('/dashboard')} variant="outline" data-testid="back-to-dashboard-btn">
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
